import type { LocalDate } from '@js-joda/core';
import type { Instrument } from '@thinkalpha/common/schemas/instruments.js';
import type {
    Idea,
    IfThenResearchPlan,
    ScannerPlan,
    ScreenerPlan,
    WatchlistPlan,
} from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import type { AlertsWidgetModel } from '@thinkalpha/platform-ws-client/contracts/workspace.js';
import type {
    AccountsWidgetModel,
    BacktestWidgetModel,
    AlphaEyeWidgetModel,
    AlphaLensWidgetModel,
    ExecutionsWidgetModel,
    Layout,
    LocatesWidgetModel,
    LocatesV2WidgetModel,
    MessagesWidgetModel,
    OrderBlotterWidgetModel,
    OrdersWidgetModel,
    PositionsWidgetModel,
    StrategyWidgetModel,
    TableWidgetModel,
    WidgetContainer,
    WidgetTab,
    Workspace,
    SearchAlphaWidgetModel,
    ScreenerWidgetModel,
    ScannerWidgetModel,
    NewsWidgetModel,
    EventsWidgetModel,
    FilingsWidgetModel,
    WatchlistWidgetModel,
    NotepadWidgetModel,
    PlatformsWidgetModel,
    TradingViewChartWidgetModel,
    TradeProWidgetModel,
    TradersWidgetModel,
    BasketTradingWidgetModel,
    SymbolsWidgetModel,
} from '@thinkalpha/ui-types/contracts/workspace';

// eslint-disable-next-line @typescript-eslint/consistent-type-exports -- re-export
export * from './sagaContext';

export interface WorkspaceViewModel extends Omit<Workspace, 'containers' | 'channels'> {
    containerIds: string[];
    activeContainerId?: string;
    isDirty: boolean;
    isDefaultWorkspace: boolean;
    activeBorderColor: string;
    /** True for locally-created workspaces not yet persisted to the API. */
    isUnsaved: boolean;
    /** The request that started this unsaved workspace; entering that request again names it. */
    newWorkspaceRequestId?: string;
    /** Ephemeral mosaic template (not persisted). Erased on drag/resize. */
    mosaicTemplate?: {
        slots: Array<{ id: string; rect: { x: number; y: number; w: number; h: number } }>;
        occupiedSlotIds: string[];
        slotContainerMap: Record<string, string>;
    };
}

export interface ContainerViewModel extends Omit<WidgetContainer, 'tabs' | 'layout'> {
    tabIds: string[];
}

export interface ContainerLayoutViewModel extends Layout {
    containerId: string;
}

export interface StrategyWidgetViewModel extends StrategyWidgetModel {}

export interface AlertsWidgetViewModel extends Omit<AlertsWidgetModel, 'keyboardCommands'> {
    linkedSymbol?: string | null;
    activeAlertsTab?: 'symbols' | 'ideas';
}

export interface BacktestWidgetViewModel extends BacktestWidgetModel {}

export interface NotepadWidgetViewModel extends NotepadWidgetModel {}

export interface TradersWidgetViewModel extends TradersWidgetModel {}

export interface PlatformsWidgetViewModel extends PlatformsWidgetModel {}

export interface SymbolsWidgetViewModel extends SymbolsWidgetModel {}

export interface TradingViewChartWidgetViewModel extends TradingViewChartWidgetModel {}

export interface AlphaLensWidgetViewModel extends AlphaLensWidgetModel {}

export interface AccountsWidgetViewModel extends AccountsWidgetModel {}

export interface OrderBlotterWidgetViewModel extends OrderBlotterWidgetModel {
    instrument: Instrument;
}

export interface TableWidgetViewModel extends TableWidgetModel {}

export interface LocatesWidgetViewModel extends LocatesWidgetModel {}

export interface LocatesV2WidgetViewModel extends LocatesV2WidgetModel {}

export interface TradeProWidgetViewModel extends TradeProWidgetModel {}

export interface BasketTradingWidgetViewModel extends BasketTradingWidgetModel {}

export type DefaultIdeaStatus = 'loading' | 'loaded' | 'failed';

export type DeferredIdeaHydration<TSource> =
    | {
          status: 'deferred' | 'loading' | 'failed';
          source: TSource;
      }
    | {
          status: 'ready';
          source?: TSource;
      };

export interface WatchlistWidgetViewModel extends Omit<WatchlistWidgetModel, 'idea'> {
    // UI always materializes the idea
    idea: Idea & { plan: WatchlistPlan };
    isIdeaDirty?: boolean;
    defaultIdeaStatus?: DefaultIdeaStatus;
    ideaHydration?: DeferredIdeaHydration<WatchlistWidgetModel['idea']>;
}

export interface MessagesWidgetViewModel extends MessagesWidgetModel {
    instrument: Instrument;
}

export interface PositionsWidgetViewModel extends PositionsWidgetModel {
    instrument: Instrument;
}

export interface ExecutionsWidgetViewModel extends ExecutionsWidgetModel {
    instrument: Instrument;
}

export interface OrdersWidgetViewModel extends OrdersWidgetModel {
    instrument: Instrument;
    isManualReviewSoundAlertEnabled: boolean;
}

export interface EventsWidgetViewModel extends EventsWidgetModel {
    gotoDate: LocalDate | null;
    displayingCalendar: boolean;
    splitPercentage: number;
}

export interface NewsWidgetViewModel extends NewsWidgetModel {}

export interface ScreenerWidgetViewModel extends Omit<ScreenerWidgetModel, 'idea'> {
    // UI always materializes the idea
    idea: Idea & { plan: ScreenerPlan };
    isIdeaDirty?: boolean;
    ideaHydration?: DeferredIdeaHydration<ScreenerWidgetModel['idea']>;
}

export interface ScannerWidgetViewModel extends Omit<ScannerWidgetModel, 'idea'> {
    // UI always materializes the idea
    idea: Idea & { plan: ScannerPlan };
    isIdeaDirty?: boolean;
    defaultIdeaStatus?: DefaultIdeaStatus;
    ideaHydration?: DeferredIdeaHydration<ScannerWidgetModel['idea']>;
}

export interface SearchAlphaWidgetViewModel extends Omit<SearchAlphaWidgetModel, 'idea'> {
    // UI always materializes the idea
    idea: Idea & { plan: IfThenResearchPlan };
    isIdeaDirty?: boolean;
    ideaHydration?: DeferredIdeaHydration<SearchAlphaWidgetModel['idea']>;
}

export interface FilingsWidgetViewModel extends FilingsWidgetModel {
    gotoDate: LocalDate | null;
}

export type LegacyWidgetViewModel =
    | AccountsWidgetViewModel
    | AlertsWidgetViewModel
    | BacktestWidgetViewModel
    | AlphaLensWidgetViewModel
    | ExecutionsWidgetViewModel
    | LocatesWidgetViewModel
    | LocatesV2WidgetViewModel
    | MessagesWidgetViewModel
    | OrderBlotterWidgetViewModel
    | TradeProWidgetViewModel
    | OrdersWidgetViewModel
    | PositionsWidgetViewModel
    | ScannerWidgetViewModel
    | ScreenerWidgetViewModel
    | SearchAlphaWidgetViewModel
    | WatchlistWidgetViewModel
    | BasketTradingWidgetViewModel;

export type AlphaEyeWidgetViewModel = Pick<AlphaEyeWidgetModel, 'type' | 'channelId'> &
    Partial<Omit<AlphaEyeWidgetModel, 'type' | 'channelId' | 'keyboardCommands'>>;

export type ResultsWidgetViewModel = Pick<StrategyWidgetViewModel, 'type' | 'channelId'> &
    Partial<Omit<StrategyWidgetViewModel, 'type' | 'channelId'>>;

export type SelfOwnedWidgetViewModel =
    | (Pick<FilingsWidgetModel, 'type' | 'channelId'> & Partial<Omit<FilingsWidgetModel, 'type' | 'channelId'>>)
    | (Pick<EventsWidgetModel, 'type' | 'channelId'> & Partial<Omit<EventsWidgetModel, 'type' | 'channelId'>>)
    | (Pick<NewsWidgetModel, 'type' | 'channelId'> & Partial<Omit<NewsWidgetModel, 'type' | 'channelId'>>)
    | AlphaEyeWidgetViewModel
    | ResultsWidgetViewModel
    | { type: 'notepad'; channelId: string | null }
    | { type: 'table'; channelId: string | null }
    | { type: 'traders'; channelId: string | null }
    | { type: 'platforms'; channelId: string | null }
    | { type: 'symbols'; channelId: string | null }
    | { type: 'tradingview-chart'; channelId: string | null };

export type WidgetViewModel = LegacyWidgetViewModel | SelfOwnedWidgetViewModel;

export type WidgetViewModelKinds = Array<WidgetViewModel['type']>[number];

export type InferWidgetViewModel<T extends WidgetViewModelKinds> = Extract<WidgetViewModel, { type: T }>;

export interface WidgetTabViewModel<T extends WidgetViewModel = WidgetViewModel> extends Omit<WidgetTab, 'widget'> {
    containerId: string;
    channelId: string | null;
    isActive: boolean;
    widget: T;
    // Poorly named dummy properties to not forget future plans
    // channelSubscriptionSettings?: {
    //     dontPublishTo?: WidgetType[];
    //     dontListenTo?: WidgetType[];
    //     publicationPriorities: [];
    // };
}

export const ApplicationMenu = {
    loggedOutOpen: 'logged-out::open',
    loggedOutClosed: 'logged-out::closed',
    dashboard: 'dashboard',
};

export type ApplicationMenuType = (typeof ApplicationMenu)[keyof typeof ApplicationMenu];

export type VerifyLeavingWorkspaceModal = {
    type: 'verify-leaving-workspace';
};

export type VerifySavingWorkspaceModal = {
    type: 'verify-saving-workspace';
};

export type GetNewWorkspaceNameModal = { type: 'get-new-workspace-name'; title: string };
export type WorkspaceLibraryModal = { type: 'workspace-library' };

export type ModalInstance =
    | VerifyLeavingWorkspaceModal
    | VerifySavingWorkspaceModal
    | GetNewWorkspaceNameModal
    | WorkspaceLibraryModal;

export interface ClientCredentials {
    clientId: string;
    clientSecret: string;
}
